/**
 * Shared helpers for mapping backend upload/pipeline messages to progress percent and stage.
 * Pipeline holistic bands: Frame extraction 0–2%, SfM drift 2–72%, SfM finalize 72–75%,
 * Gaussian 75–92%, Export 92–95%, Semantic index 95–99%, Complete 100%.
 */

/** Unified SfM (hloc + COLMAP) occupies the holistic bar from 5% → 75%. */
export const SFM_PERCENT_START = 5;
export const SFM_PERCENT_END = 75;
/** SfM wall-clock drift ceiling (backend emits milestones 72–74 before jump to 75). */
export const SFM_DRIFT_PERCENT_END = 72;
/** Gaussian training band end before export. */
export const GAUSSIAN_PERCENT_END = 92;
export const EXPORT_PERCENT_START = 92;
export const EXPORT_PERCENT_END = 95;
export const SEMANTIC_PERCENT_START = 95;

/** Observed SAM 3 stage after Gaussian export: model load + up to 24 keyframes + fusion/upload (~1.5–3 min). */
export const SAM3_SEMANTIC_INDEX_MINUTES = 3;

/** User-visible message when an in-tab upload cannot be resumed (reload / tab discard). */
export const UPLOAD_INTERRUPTED_MESSAGE = 'Upload interrupted. Please try again.';

/** Firestore shows an in-flight pipeline run — stale failedAt from a prior attempt should be ignored. */
export function isActivePipelineRunStatus(status) {
  const s = (status || '').toLowerCase();
  return s === 'processing' || s === 'queued';
}

function pipelineHasAdvancedPastUpload(capture) {
  const frames = capture?.processing?.frames;
  if (frames != null && Number(frames) > 0) return true;
  const stage = String(capture?.processing?.stage || '').toLowerCase();
  if (!stage || stage === 'upload' || stage === 'uploadpending' || stage === 'queued') return false;
  return true;
}

/** Age since the latest metadata touch — re-upload / re-queue refreshes updatedAt, not createdAt. */
function resolveProcessingStallAgeMs(capture) {
  const updated = capture?.updatedAt?.toMillis?.() ?? 0;
  const created = capture?.createdAt?.toMillis?.() ?? 0;
  const anchor = updated > 0 ? updated : created;
  return anchor ? Date.now() - anchor : Number.POSITIVE_INFINITY;
}

/** Mirror backend `STARLING_FRAMES_PER_SEC` / `STARLING_MIN_FRAMES` (backend/app.py). */
const PIPELINE_FRAMES_PER_SEC = 2;
const PIPELINE_MIN_FRAMES = 100;
/** When ffprobe duration is missing, backend uses `NUM_FRAMES_TARGET` (default 150). */
const PIPELINE_DEFAULT_FRAME_TARGET = 150;

/**
 * Expected SfM frame budget from source duration — matches backend `compute_frames_target`.
 * Short clips still hit {@link PIPELINE_MIN_FRAMES}, which dominates wall-clock time.
 */
export function estimatePipelineFrameTarget(durationSeconds) {
  const d = Number(durationSeconds);
  if (!Number.isFinite(d) || d <= 0) return PIPELINE_DEFAULT_FRAME_TARGET;
  return Math.max(PIPELINE_MIN_FRAMES, Math.round(d * PIPELINE_FRAMES_PER_SEC));
}

function parseResolutionPixels(sourceVideoResolutionFps) {
  if (typeof sourceVideoResolutionFps !== 'string') return null;
  const match = sourceVideoResolutionFps.match(/(\d{2,5})\s*x\s*(\d{2,5})/i);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return width * height;
}

/**
 * Total pipeline ETA (upload → SfM → Gaussian → export → SAM 3 semantic index).
 * Calibrated to backend frame budget: short clips still process ~100+ frames (SfM + 10k Gaussian iters).
 * Rounded to nearest 5 minutes; includes {@link SAM3_SEMANTIC_INDEX_MINUTES} for indexing.
 */
export function estimateProcessingMinutes({ durationSeconds, sourceVideoResolutionFps }) {
  const durationSec = Number(durationSeconds);
  const hasDuration = Number.isFinite(durationSec) && durationSec > 0;
  if (!hasDuration) return null;

  const frameTarget = estimatePipelineFrameTarget(durationSec);
  const baselinePixels = 1920 * 1080;
  const pixelCount = parseResolutionPixels(sourceVideoResolutionFps) ?? baselinePixels;
  const resolutionFactor = Math.min(2.4, Math.max(0.75, Math.sqrt(pixelCount / baselinePixels)));

  /** Gaussian iteration floor + SfM setup + export — significant even for sub‑10s clips. */
  const FIXED_PIPELINE_MINUTES = 25;
  /** SfM + training load scales with COLMAP frame budget. */
  const MINUTES_PER_FRAME = 0.14;
  /** Upload + decode overhead on longer source files. */
  const MINUTES_PER_SOURCE_MINUTE = 1.5;

  const sourceMinutes = durationSec / 60;
  const coreMinutes =
    (FIXED_PIPELINE_MINUTES + frameTarget * MINUTES_PER_FRAME + sourceMinutes * MINUTES_PER_SOURCE_MINUTE) *
    resolutionFactor;
  const estimatedMinutes = coreMinutes + SAM3_SEMANTIC_INDEX_MINUTES;
  return Math.min(300, Math.max(15, Math.ceil(estimatedMinutes / 5) * 5));
}

/** Human-readable byte count for upload subtitle (e.g. `42 MB / 180 MB`). */
export function formatBytes(n) {
  const bytes = Number.isFinite(n) ? Math.max(0, n) : 0;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`;
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`;
  return `${Math.round(bytes)} B`;
}

/**
 * Map an internal 0–100 ratio within the SfM subprocess to the holistic SfM band (legacy / rare use).
 */
export function mapSfmRatioToOverallPercent(ratio = 0) {
  const safeRatio = Number.isFinite(ratio) ? ratio : 0;
  const clampedRatio = Math.max(0, Math.min(100, safeRatio));
  const mapped =
    SFM_PERCENT_START +
    ((SFM_PERCENT_END - SFM_PERCENT_START) * (clampedRatio / 100));
  return Math.round(mapped);
}

export function resolvePercentFromMessage(message = '') {
  const normalized = (message || '').toLowerCase();
  if (normalized.includes('updating live viewer') || normalized.includes('viewer ready')) return 100;
  if (normalized.includes('training complete') && normalized.includes('export ready')) return 100;
  if (normalized.includes('indexing scene objects')) return 96;
  if (normalized.includes('uploading new splat to cloud') || normalized.includes('uploading new capture to cloud')) return 94;
  if (normalized.includes('exporting gaussians') || normalized.includes('exporting gaussian splat') || normalized.includes('exporting gaussian capture')) return 93;
  if (normalized.includes('generating gaussians') || normalized.includes('gaussian training in progress')) return 85;
  if (normalized.includes('gaussian')) return 76;
  if (
    normalized.includes('structure from motion')
    || normalized.includes('running structure from motion')
    || normalized.includes('running 3d reconstruction')
    || normalized.includes('sfm in progress')
    || (normalized.includes('sfm') && normalized.includes('complete'))
  ) {
    return 40;
  }
  if (normalized.includes('sfm process started') || (normalized.includes('initiating') && normalized.includes('sfm'))) return SFM_PERCENT_START;
  if ((normalized.includes('frame') && normalized.includes('extract')) || normalized.includes('extracting frames')) return 4;
  if (normalized.includes('upload complete')) return 3;
  if (normalized.includes('uploading video') || normalized.includes('received upload request') || normalized.includes('upload')) return 2;
  return 1;
}

export function messageToStage(message = '') {
  const n = (message || '').toLowerCase();
  if (n.includes('viewer ready') || (n.includes('training complete') && n.includes('export ready'))) return 6;
  if (n.includes('indexing scene objects')) return 5;
  if (n.includes('exporting gaussians')
    || n.includes('preparing viewer')
    || (n.includes('exporting') && n.includes('uploading'))
    || n.includes('exporting gaussian splat')
    || n.includes('exporting gaussian capture')
    || n.includes('uploading new splat to cloud')
    || n.includes('uploading new capture to cloud')) return 5;
  if (n.includes('gaussian') || (n.includes('initiating') && n.includes('training')) || n.includes('generating gaussians')) return 4;
  if (
    n.includes('structure from motion')
    || n.includes('running structure from motion')
    || n.includes('running 3d reconstruction')
    || n.includes('sfm in progress')
    || (n.includes('sfm') && n.includes('complete'))
  ) {
    return 3;
  }
  if (n.includes('sfm process started') || (n.includes('initiating') && n.includes('sfm'))) return 3;
  if ((n.includes('frame') && n.includes('extract')) || n.includes('extracting frames')) return 2;
  if (n.includes('upload') && !n.includes('complete')) return 1;
  if (n.includes('upload') && n.includes('complete')) return 1;
  return 0;
}

export function normalizeProgressMessage(message = '') {
  const n = (message || '').toLowerCase();
  if (!n) return 'UPLOADING VIDEO';
  if (n.includes('setting up pipeline')) return 'SETTING UP PIPELINE';
  if (n.includes('in queue') || n === 'queued') return 'QUEUEING';
  if (n.includes('indexing scene objects')) return 'INDEXING SCENE OBJECTS';
  if (n.includes('training complete') && n.includes('export ready')) return 'EXPORTING GAUSSIANS';
  if (
    n.includes('exporting gaussians')
    || n.includes('uploading new splat to cloud')
    || n.includes('uploading new capture to cloud')
    || n.includes('exporting gaussian splat')
    || n.includes('exporting gaussian capture')
    || n.includes('preparing viewer')
  ) return 'EXPORTING GAUSSIANS';
  if (n.includes('generating gaussians') || n.includes('gaussian') || (n.includes('initiating') && n.includes('training'))) return 'GENERATING GAUSSIANS';
  if (
    n.includes('structure from motion')
    || n.includes('running structure from motion')
    || n.includes('running 3d reconstruction')
    || n.includes('sfm in progress')
    || (n.includes('sfm') && n.includes('complete'))
  ) {
    return 'STRUCTURE FROM MOTION';
  }
  if (n.includes('sfm process started') || (n.includes('frame') && n.includes('extract')) || n.includes('extracting frames') || ((n.includes('initiating') && n.includes('sfm')))) return 'EXTRACTING FRAMES';
  if (n.includes('upload') || n.includes('received upload request')) return 'UPLOADING VIDEO';
  return message.toString().toUpperCase();
}

export function normalizeStatusTitle(title = '') {
  const normalized = (title || '').toString().trim().toUpperCase();
  if (!normalized) return '';
  if (normalized === 'UPLOAD') return 'UPLOADING';
  if (normalized === 'IN QUEUE' || normalized === 'QUEUED') return 'QUEUEING';
  return normalized;
}

export function getInterruptedServersOfflineTitle() {
  return 'FAILED: SERVERS OFFLINE';
}

/**
 * Cap percent by current stage (truthful/steady profile).
 */
export function getStagePercentCap(currentStage) {
  if (currentStage == null || currentStage < 0) return 0;
  if (currentStage <= 1) return 3;
  if (currentStage <= 2) return 5;
  if (currentStage <= 3) return SFM_PERCENT_END;
  if (currentStage <= 4) return GAUSSIAN_PERCENT_END;
  return 100;
}

/**
 * Interpolation-safe ceiling for synthetic UI progress.
 */
export function getSyntheticStagePercentCap(currentStage) {
  return getStagePercentCap(currentStage);
}

/**
 * Start/end percent for each stage segment (for interpolation ramp).
 */
export function getStagePercentRange(stageIndex) {
  if (stageIndex == null || stageIndex < 0) return { start: 0, end: 3 };
  if (stageIndex <= 1) return { start: 0, end: 3 };
  if (stageIndex <= 2) return { start: 3, end: 5 };
  if (stageIndex <= 3) return { start: SFM_PERCENT_START, end: SFM_PERCENT_END };
  if (stageIndex <= 4) return { start: 75, end: GAUSSIAN_PERCENT_END };
  return { start: EXPORT_PERCENT_START, end: 100 };
}

/**
 * Base ramp duration in ms for logarithmic drift within a stage.
 */
export function getRampMsForStage(stageIndex) {
  if (stageIndex == null || stageIndex < 0) return 0;
  if (stageIndex <= 1) return 30 * 1000;
  if (stageIndex <= 2) return 30 * 1000;
  if (stageIndex <= 3) return 20 * 60 * 1000;
  if (stageIndex <= 4) return 8 * 60 * 1000;
  return 0;
}

/**
 * Logarithmic interpolation toward ~90% of remaining range to stage ceiling (all stages).
 */
export function getLogarithmicInterpolatedPercent({
  currentPercent = 0,
  confirmedPercent = 0,
  elapsedMs = 0,
  stageIndex = 0,
}) {
  const { end: stageCeiling } = getStagePercentRange(stageIndex);
  const rampMs = getRampMsForStage(stageIndex);
  const safeCurrent = Number.isFinite(currentPercent) ? currentPercent : 0;
  const safeConfirmed = Number.isFinite(confirmedPercent) ? confirmedPercent : 0;
  const safeElapsed = Math.max(0, Number.isFinite(elapsedMs) ? elapsedMs : 0);

  if (rampMs <= 0 || safeConfirmed >= stageCeiling) return safeCurrent;

  const remaining = stageCeiling - safeConfirmed;
  const target = safeConfirmed + remaining * 0.9;
  if (target <= safeCurrent) return safeCurrent;

  const t = Math.min(safeElapsed, rampMs);
  const ratio = Math.log1p(t) / Math.log1p(rampMs);
  const interpolated = safeConfirmed + (target - safeConfirmed) * ratio;
  return Math.max(safeCurrent, Math.min(target, interpolated));
}

/**
 * @deprecated Use getLogarithmicInterpolatedPercent with stageIndex 3 for SfM-only.
 */
export function getInterpolatedSfmPercent({
  currentPercent = 0,
  confirmedPercent = 0,
  elapsedMs = 0,
}) {
  return getLogarithmicInterpolatedPercent({
    currentPercent,
    confirmedPercent,
    elapsedMs,
    stageIndex: 3,
  });
}

/**
 * Infer failStage from error message for backward compatibility when backend does not send failStage.
 */
export function inferFailStageFromError(error = '') {
  const n = (error || '').toLowerCase();
  if (n.includes('sfm') && n.includes('fail')) return 'SfM';
  if (n.includes('gaussian') && n.includes('train')) return 'GaussianTraining';
  if (n.includes('gaussian') && n.includes('export')) return 'GaussianExport';
  if (n.includes('export') && (n.includes('config') || n.includes('not found'))) return 'GaussianExport';
  if (n.includes('frame') && n.includes('extract')) return 'FrameExtraction';
  if (n.includes('upload') || n.includes('video') || n.includes('download')) return 'Upload';
  return 'Unknown';
}

/**
 * Map failedAt/failStage to stage index (0-3) for dot indicator.
 * Upload=0, FrameExtraction=1, SfM=2, GaussianTraining/GaussianExport=3.
 */
export function getFailedStageIndex(failedAt) {
  if (!failedAt || typeof failedAt !== 'string') return -1;
  const n = failedAt.toLowerCase();
  if (n.includes('upload')) return 0;
  if (n.includes('frame') && n.includes('extract')) return 1;
  if (n.includes('sfm')) return 2;
  if (n.includes('gaussian') || n.includes('export')) return 3;
  return -1;
}

/**
 * Short failure reason for LoadingUI subtitle (2–3 words).
 * Returns null only when nothing can be inferred (use FAILED_UNKNOWN_REASON).
 */
export const FAILED_UNKNOWN_REASON = 'Unknown reason';

/** Firestore processing.failedAt when RunPod redispatch cap is reached (matches Cloud Functions). */
export const GPU_WORKER_UNAVAILABLE_FAILED_AT = 'No healthy GPU worker available';

const STAGE_INDEX_SHORT = {
  0: 'Upload failed',
  1: 'Extraction failed',
  2: 'SfM failed',
  3: 'Training failed',
  4: 'Export failed',
  5: 'Export failed',
};

function reasonFromStageIndex(idx) {
  if (typeof idx === 'number' && idx >= 0 && STAGE_INDEX_SHORT[idx]) {
    return STAGE_INDEX_SHORT[idx];
  }
  return null;
}

function reasonFromStageToken(raw) {
  const n = (raw || '').toLowerCase().replace(/\s/g, '');
  if (!n) return null;
  if (n === 'sfm') return 'SfM failed';
  if (n.includes('gaussiantraining')) return 'Training failed';
  if (n.includes('gaussianexport') || n === 'export') return 'Export failed';
  if (n.includes('frameextraction')) return 'Extraction failed';
  if (n === 'upload' || n === 'uploadpending') return 'Upload failed';
  if (n === 'semanticindex') return 'Indexing failed';
  if (n === 'cancelled' || n === 'dispatch') return null;
  return null;
}

export function getFailureShortReason(raw, failedStageIndex = -1) {
  if (raw == null || typeof raw !== 'string' || !raw.trim()) {
    return reasonFromStageIndex(failedStageIndex);
  }

  let s = raw.trim();
  let n = s.toLowerCase();
  if (n.startsWith('error:')) {
    s = s.slice(6).trim();
    n = s.toLowerCase();
  }

  if (
    n.includes('execution time limit')
    || n.includes('time limit reached')
    || (n.includes('timed out') && !n.includes('queue'))
  ) {
    return 'GPU timed out';
  }
  if (n.includes('request timed out')) return 'Request timed out';

  if (
    n.includes('out of memory')
    || n.includes('oom')
    || n.includes('signal 9')
    || (n.includes('killed') && n.includes('signal'))
  ) {
    return 'Out of memory';
  }
  if (n.includes('process killed') || n.includes('gpu ran out of memory')) return 'Out of memory';
  if (n.includes('cuda out of memory')) return 'GPU memory full';

  if (n.includes('no healthy gpu worker')) return 'No GPU available';
  if (n.includes('worker crashed')) return 'Worker crashed';
  if (n.includes('worker stopped unexpectedly')) return 'Worker stopped';

  if (
    n.includes('too sparse')
    || n.includes('images registered')
    || n.includes('parallax/overlap')
    || (n.includes('parallax') && n.includes('overlap'))
  ) {
    return 'Too sparse';
  }
  if (n.includes('too few') && n.includes('3d point')) return 'Too few points';
  if (
    (n.includes('points3d') || n.includes('points3'))
    && (n.includes('no readable') || n.includes('missing or invalid'))
  ) {
    return 'No 3D points';
  }
  if (n.includes('transforms.json') || n.includes('no usable camera')) return 'No camera data';
  if (n.includes('too few') && n.includes('match')) return 'Too few matches';
  if (n.includes('sparse') && (n.includes('colmap') || n.includes('reconstruction'))) {
    return 'Sparse reconstruction';
  }

  if (
    n.includes('gaussian export failed')
    || n.includes('no splat')
    || n.includes('no training config')
    || (n.includes('export') && n.includes('failed'))
  ) {
    return 'Export failed';
  }

  if (
    n.includes('offline')
    || n.includes('servers')
    || n.includes('network')
    || n.includes('fetch failed')
    || n.includes('backend server')
  ) {
    return 'Servers offline';
  }

  if (n.startsWith('unknown:')) {
    if (n.includes('sfm')) return 'SfM failed';
    if (n.includes('gaussiantraining')) return 'Training failed';
    if (n.includes('gaussianexport') || n.includes('export')) return 'Export failed';
    if (n.includes('frame')) return 'Extraction failed';
    return reasonFromStageIndex(failedStageIndex);
  }

  if (n.includes('upload interrupted')) return 'Upload interrupted';
  if (n.includes('upload failed') || n.includes('storage check failed')) return 'Upload failed';
  if (n === 'pipeline failed') return reasonFromStageIndex(failedStageIndex) || 'Pipeline failed';
  if (n.includes('cancelled')) return null;

  const fromToken = reasonFromStageToken(s);
  if (fromToken) return fromToken;

  if (n.includes('timed out')) return 'Timed out';
  if (n.includes('sfm')) return 'SfM failed';
  if (n.includes('gaussian') || n.includes('generating')) return 'Training failed';
  if (n.includes('export')) return 'Export failed';
  if (n.includes('frame') && n.includes('extract')) return 'Extraction failed';

  return reasonFromStageIndex(failedStageIndex);
}

/** @deprecated Use getFailureShortReason */
export function getLaymanFailureExplanation(raw, failedStageIndex = -1) {
  return getFailureShortReason(raw, failedStageIndex);
}

/**
 * Resolve the user-visible failure explanation for LoadingUI (2–3 words).
 */
export function resolveFailureDisplayMessage(raw, { isFailed = true, failedStageIndex = -1 } = {}) {
  if (!isFailed) return null;
  const reason = getFailureShortReason(raw, failedStageIndex);
  if (reason) return reason;
  return FAILED_UNKNOWN_REASON;
}

/**
 * Resolve canonical failure snapshot from poll data first, then Firestore metadata.
 * Returns { failedStageIndex, failedPercent, failedAt } or null when not failed.
 */
export function resolveFailedSnapshot(polledData, captureMetadata) {
  const polledStatus = polledData?.status;
  const firestoreStatus = captureMetadata?.processing?.status;
  const polledFailMsg = polledData?.failedAt != null && String(polledData.failedAt).trim()
    ? String(polledData.failedAt).trim()
    : null;
  const pollIndicatesFailure = polledStatus === 'failed' || polledStatus === 'cancelled';

  if (isActivePipelineRunStatus(firestoreStatus) && !pollIndicatesFailure) {
    return null;
  }

  const isFailed = pollIndicatesFailure
    || firestoreStatus === 'failed'
    || !!(captureMetadata?.processing?.failedAt)
    || !!polledFailMsg;

  if (!isFailed) return null;

  if (
    polledFailMsg
    && polledStatus !== 'failed'
    && polledStatus !== 'cancelled'
  ) {
    if (
      polledData?.failedStageIndex != null
      && polledData.failedStageIndex >= 0
    ) {
      return {
        failedStageIndex: polledData.failedStageIndex,
        failedPercent: polledData.failedPercent ?? 0,
        failedAt: polledFailMsg,
      };
    }
    return {
      failedStageIndex: getFailedStageIndex(polledFailMsg),
      failedPercent: polledData.failedPercent ?? 0,
      failedAt: polledFailMsg,
    };
  }

  if (
    (polledStatus === 'failed' || polledStatus === 'cancelled')
    && polledData?.failedStageIndex != null
    && polledData.failedStageIndex >= 0
  ) {
    return {
      failedStageIndex: polledData.failedStageIndex,
      failedPercent: polledData.failedPercent ?? 0,
      failedAt: polledData.failedAt ?? captureMetadata?.processing?.failedAt ?? 'Unknown',
    };
  }

  if (polledStatus === 'failed' || polledStatus === 'cancelled') {
    const mergedFailedAt = polledData?.failedAt
      ?? captureMetadata?.processing?.failedAt
      ?? captureMetadata?.processing?.failStatus
      ?? (polledStatus === 'cancelled' ? 'Cancelled' : 'Processing failed');
    return {
      failedStageIndex: getFailedStageIndex(mergedFailedAt),
      failedPercent: polledData?.failedPercent ?? captureMetadata?.processing?.failedPercent ?? 0,
      failedAt: mergedFailedAt,
    };
  }

  const fsIdx = captureMetadata?.processing?.failedStageIndex;
  if (fsIdx != null && fsIdx >= 0) {
    return {
      failedStageIndex: fsIdx,
      failedPercent: captureMetadata?.processing?.failedPercent ?? 0,
      failedAt: captureMetadata?.processing?.failedAt ?? 'Unknown',
    };
  }

  const failedAt = captureMetadata?.processing?.failedAt ?? 'Unknown';
  return {
    failedStageIndex: getFailedStageIndex(failedAt),
    failedPercent: captureMetadata?.processing?.failedPercent ?? 0,
    failedAt,
  };
}

/**
 * Human-readable stage label for a pipeline stage index (0-3).
 */
export function stageIndexToLabel(idx) {
  const labels = ['UPLOAD', 'FRAME EXTRACTION', 'STRUCTURE FROM MOTION', 'GAUSSIAN TRAINING'];
  return labels[idx] ?? 'UNKNOWN';
}

/**
 * Max internal pipeline stage (1–6) allowed for a holistic progress percent — matches bands in file header.
 * Internal: 1 upload, 2 frame extraction, 3 SfM, 4 Gaussian, 5 export/prep, 6 done.
 */
export function maxInternalStageIndexForHolisticPercent(percent) {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  if (pct >= 100) return 6;
  if (pct >= GAUSSIAN_PERCENT_END) return 5;
  if (pct >= 75) return 4;
  if (pct >= SFM_PERCENT_START) return 3;
  if (pct >= 3) return 2;
  return 1;
}

/**
 * Clamp inferred Firestore/redis stage so UI cannot show Gaussian/SfM ahead of holistic % (e.g. noisy "generating" + 5%).
 */
export function clampInternalStageIndexToHolisticPercent(stageIndex, percent) {
  const s = typeof stageIndex === 'number' && !Number.isNaN(stageIndex) ? stageIndex : 0;
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  if (s < 0) return s;
  if (s >= 6) return s;
  const maxS = maxInternalStageIndexForHolisticPercent(pct);
  return Math.min(s, maxS);
}

/** LoadingUI / poll title when stage index is known (internal 1–5). Optional percent splits export vs semantic on stage 5. */
export function loadingTitleForInternalPipelineStage(internalStage, percent) {
  const s = typeof internalStage === 'number' && !Number.isNaN(internalStage) ? internalStage : 0;
  const pct = Number.isFinite(percent) ? percent : null;
  if (s <= 0) return 'PROCESSING';
  if (s <= 1) return 'UPLOAD';
  if (s === 2) return 'FRAME EXTRACTION';
  if (s === 3) return 'STRUCTURE FROM MOTION';
  if (s === 4) return 'GAUSSIAN TRAINING';
  if (s === 5) {
    if (pct != null && pct >= SEMANTIC_PERCENT_START && pct < 100) return 'INDEXING SCENE OBJECTS';
    if (pct != null && pct >= EXPORT_PERCENT_START) return 'EXPORTING GAUSSIANS';
    return 'EXPORTING GAUSSIANS';
  }
  return 'RECONSTRUCTION COMPLETE';
}

/** Pill title from holistic pipeline percent and optional backend message. */
export function loadingTitleForPipelinePercent(percent, message = '') {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const m = (message || '').toLowerCase();
  if (pct >= 100 || m.includes('viewer ready') || (m.includes('training complete') && m.includes('export ready'))) {
    return 'RECONSTRUCTION COMPLETE';
  }
  if (pct >= SEMANTIC_PERCENT_START || m.includes('indexing scene objects')) return 'INDEXING SCENE OBJECTS';
  if (pct >= EXPORT_PERCENT_START || m.includes('exporting gaussians') || m.includes('exporting gaussian')) {
    return 'EXPORTING GAUSSIANS';
  }
  if (m.includes('generating gaussians') || m.includes('gaussian') || pct >= 75) return 'GAUSSIAN TRAINING';
  if (
    m.includes('structure from motion')
    || m.includes('running 3d reconstruction')
    || m.includes('sfm')
    || pct >= SFM_PERCENT_START
  ) {
    return 'STRUCTURE FROM MOTION';
  }
  if (m.includes('extracting frames') || m.includes('frame') || pct >= 2) return 'FRAME EXTRACTION';
  if (
    m.includes('in queue')
    || m === 'queued'
    || m.includes('setting up pipeline')
    || m.includes('setting up reconstruction')
  ) {
    return 'QUEUEING';
  }
  return 'PROCESSING';
}

/**
 * When Firestore has a frame count, extraction is done for UX even if Redis still reports
 * upload/extraction. Bump to stage 4 (SfM) so the top pipeline, LoadingUI, and % interpolation match.
 * Does not change queued (-1) or Gaussian/export (≥4) stages.
 */
export function alignPipelineStageWithExtractedFrames(stageIndex, framesCount, pipelineBusy) {
  const frames = Number(framesCount);
  const s = typeof stageIndex === 'number' && !Number.isNaN(stageIndex) ? stageIndex : 0;
  if (!pipelineBusy) return s;
  if (!Number.isFinite(frames) || frames <= 0) return s;
  if (s < 0) return s;
  if (s >= 3) return s;
  return 3;
}

/**
 * Map currentStage (internal index 1–6) to pipeline stage index (0–3) for Reconstruction Pipeline bar.
 * 1→0 (Upload); 2→1 (Frame Extraction); 3→2 (SfM); 4→3 (Gaussian); 5+→4 (all segments done).
 */
export function getPipelineStageIndex(currentStage) {
  if (currentStage == null || currentStage <= 1) return 0;
  if (currentStage <= 2) return 1;
  if (currentStage <= 3) return 2;
  if (currentStage <= 4) return 3;
  return 4;
}

/**
 * Compute active and failed indices for Reconstruction Pipeline.
 * Returns { activeIndex: 0–4, failedIndex: 0–3 | -1 }.
 */
export function stageIndexForPipeline(currentStage, status, failedAt) {
  const failedIndex = getFailedStageIndex(failedAt);
  if (status === 'failed' && failedIndex >= 0) {
    return { activeIndex: failedIndex, failedIndex };
  }
  if (status === 'completed') {
    return { activeIndex: 4, failedIndex: -1 };
  }
  const activeIndex = getPipelineStageIndex(currentStage);
  return { activeIndex, failedIndex: -1 };
}

/**
 * Get standardized error message for LoadingUI.
 */
export function getErrorMessage(failedAt, uploadError, backendOffline, failedStageIndex = -1) {
  if (backendOffline) return 'Servers offline';
  return resolveFailureDisplayMessage(uploadError || failedAt, { isFailed: true, failedStageIndex })
    || FAILED_UNKNOWN_REASON;
}

/**
 * Short failure reason for pipeline header / overlay (same as LoadingUI subtitle).
 */
export function getFailedAtStageMessage(failedAt, uploadError, backendOffline, failedStageIndex = -1) {
  if (backendOffline) return 'Servers offline';
  return resolveFailureDisplayMessage(uploadError || failedAt, { isFailed: true, failedStageIndex })
    || FAILED_UNKNOWN_REASON;
}

/**
 * Merge Redis stage key, Firestore progress message, and holistic % into one display stage.
 * Used by usePipelinePolling so LoadingUI and ReconstructionPipeline stay aligned.
 */
export function resolvePolledPipelineDisplay({
  redisStage = '',
  percent = 0,
  progressMessage = '',
  framesCount,
  pipelineBusy = false,
}) {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const effectiveStage = (redisStage || '').trim() || 'queued';
  const fromRedis = mapRedisStageToFrontend(effectiveStage, pct);
  let stageIndex = fromRedis.stageIndex;
  let stageTitle = fromRedis.stageTitle;

  const msg = (progressMessage || '').trim();
  if (msg) {
    const fromMsg = messageToStage(msg);
    if (fromMsg > stageIndex) {
      stageIndex = fromMsg;
      stageTitle = loadingTitleForPipelinePercent(pct, msg);
    }
    const titleFromMsg = loadingTitleForPipelinePercent(pct, msg);
    if (titleFromMsg !== 'PROCESSING') {
      stageTitle = titleFromMsg;
    }
  }

  const preAlign = stageIndex;
  let displayStageIndex = alignPipelineStageWithExtractedFrames(
    stageIndex,
    framesCount,
    pipelineBusy,
  );
  let displayStageTitle =
    displayStageIndex !== preAlign && preAlign >= 0 && preAlign < 3
      ? 'STRUCTURE FROM MOTION'
      : stageTitle;

  const alignedBeforeClamp = displayStageIndex;
  displayStageIndex = clampInternalStageIndexToHolisticPercent(displayStageIndex, pct);

  // Stale low % must not undo frames-based SfM promotion (logs can be ahead of Firestore).
  const frames = Number(framesCount);
  if (
    pipelineBusy
    && alignedBeforeClamp >= 3
    && preAlign < 3
    && Number.isFinite(frames)
    && frames > 0
    && displayStageIndex < alignedBeforeClamp
  ) {
    displayStageIndex = alignedBeforeClamp;
    displayStageTitle = 'STRUCTURE FROM MOTION';
  }

  // Redis stage key (local /pipeline/status) is authoritative when ahead of stale percent.
  if (pipelineBusy && fromRedis.stageIndex >= 3 && displayStageIndex < fromRedis.stageIndex) {
    displayStageIndex = fromRedis.stageIndex;
    displayStageTitle = fromRedis.stageTitle;
  }

  if (displayStageIndex !== alignedBeforeClamp && displayStageIndex === clampInternalStageIndexToHolisticPercent(alignedBeforeClamp, pct)) {
    displayStageTitle = loadingTitleForInternalPipelineStage(displayStageIndex, pct);
  }

  let displayPercent = pct;
  const sfmNudge =
    displayStageIndex === 3
    && preAlign >= 0
    && preAlign < 3
    && pipelineBusy;
  if (sfmNudge) {
    displayPercent = Math.max(pct, SFM_PERCENT_START);
  }

  return {
    rawStageIndex: fromRedis.stageIndex,
    stageIndex: displayStageIndex,
    stageTitle: displayStageTitle,
    displayPercent,
  };
}

/**
 * Map Redis pipeline stage string to frontend stage index and title.
 * Used by usePipelinePolling to translate backend progress into LoadingUI props.
 */
export function mapRedisStageToFrontend(stage, percent) {
  const s = (stage || '').toLowerCase();
  const pct = Number.isFinite(percent) ? percent : 0;
  if (s === 'done') return { stageIndex: 6, stageTitle: 'RECONSTRUCTION COMPLETE' };
  if (s === 'semanticindex') {
    return { stageIndex: 5, stageTitle: pct >= SEMANTIC_PERCENT_START ? 'INDEXING SCENE OBJECTS' : 'EXPORTING GAUSSIANS' };
  }
  if (s === 'gaussianexport' || s === 'sogconversion') {
    return { stageIndex: 5, stageTitle: pct >= SEMANTIC_PERCENT_START ? 'INDEXING SCENE OBJECTS' : 'EXPORTING GAUSSIANS' };
  }
  if (s === 'gaussiantraining') return { stageIndex: 4, stageTitle: 'GAUSSIAN TRAINING' };
  if (s === 'sfmcomplete') return { stageIndex: 3, stageTitle: 'STRUCTURE FROM MOTION' };
  if (s === 'sfm') return { stageIndex: 3, stageTitle: 'STRUCTURE FROM MOTION' };
  if (s === 'frameextraction') {
    return { stageIndex: 2, stageTitle: 'FRAME EXTRACTION' };
  }
  if (s.includes('frame') && (s.includes('extract') || s.includes('extraction'))) {
    return { stageIndex: 2, stageTitle: 'FRAME EXTRACTION' };
  }
  if (s === 'video2images' || s === 'extracting' || s === 'extraction') {
    return { stageIndex: 2, stageTitle: 'FRAME EXTRACTION' };
  }
  if (s === 'upload' || s === 'uploadpending') return { stageIndex: 1, stageTitle: 'UPLOAD' };
  if (s === 'queued') return { stageIndex: -1, stageTitle: 'QUEUEING' };
  if (pct >= SEMANTIC_PERCENT_START && pct < 100) {
    return { stageIndex: 5, stageTitle: 'INDEXING SCENE OBJECTS' };
  }
  if (pct >= EXPORT_PERCENT_START && pct < 100) {
    return { stageIndex: 5, stageTitle: 'EXPORTING GAUSSIANS' };
  }
  return { stageIndex: 0, stageTitle: 'PROCESSING' };
}

/**
 * Progress % for a capture row on the projects list (active upload context or Firestore progress).
 */
export function resolveCaptureListPercent(capture, {
  activeCaptureId,
  uploadPhase,
  uploadPercent,
  pipelinePercent,
  progressPercent,
}) {
  if (!capture?.id) return null;
  if (capture.id === activeCaptureId) {
    if (uploadPhase === 'uploading') return uploadPercent;
    if (uploadPhase === 'queueing' || uploadPhase === 'pipeline' || uploadPhase === 'done') {
      return pipelinePercent;
    }
    return progressPercent;
  }
  const fsPct = capture.progress?.percent;
  return Number.isFinite(Number(fsPct)) ? Number(fsPct) : null;
}

/**
 * Processing rows must always show a numeric %; missing % after a grace window means stalled.
 * Returns { percent, stalled } — percent may be 0 during the grace window when backend has not written yet.
 */
export function resolveCaptureListDisplayPercent(capture, uploadCtx, {
  graceMs = 2 * 60 * 1000,
} = {}) {
  const raw = resolveCaptureListPercent(capture, uploadCtx);
  if (raw != null && Number.isFinite(raw)) {
    return { percent: raw, stalled: false };
  }
  const status = capture?.processing?.status || 'processing';
  if (status !== 'processing') return { percent: null, stalled: false };
  if (pipelineHasAdvancedPastUpload(capture)) {
    return { percent: 0, stalled: false };
  }
  const ageMs = resolveProcessingStallAgeMs(capture);
  if (ageMs < graceMs) return { percent: 0, stalled: false };
  return { percent: null, stalled: true };
}

/**
 * Shared UI state contract for capture cards and LoadingUI title selection.
 */
export function deriveCaptureUiState({
  processingStatus,
  failedAt,
  failStatus,
  uploadError,
  backendOffline,
  stageTitle,
  persistedFailedStageIndex,
  splatPath,
}) {
  const status = (processingStatus || 'processing').toLowerCase();
  const isDelivered = !!splatPath && (status === 'completed' || status === 'processing');
  const failStage = failedAt ?? failStatus ?? null;
  const hasUploadError = typeof uploadError === 'string' && uploadError.trim().length > 0;
  const hasFailedStage = !!failStage && !isActivePipelineRunStatus(status);
  const offlineByError = hasUploadError && /offline|servers|fetch|network/i.test(uploadError);
  const isInterrupted = !!backendOffline || offlineByError;
  const isFailed = !isDelivered && (status === 'failed' || hasUploadError || hasFailedStage);
  const resolvedStageTitle = normalizeStatusTitle(stageTitle);
  const derivedFailedIdx = getFailedStageIndex(
    failStage || (isInterrupted ? 'Upload' : (hasUploadError ? inferFailStageFromError(uploadError) : null))
  );
  const failedStageIndex = persistedFailedStageIndex != null && persistedFailedStageIndex >= 0
    ? persistedFailedStageIndex
    : derivedFailedIdx;

  if (status === 'completed' || isDelivered) {
    return {
      uiState: 'completed',
      titleText: 'RECONSTRUCTION COMPLETE',
      messageText: '',
      failedStageIndex: -1,
      isFailedForUI: false,
      interrupted: false,
    };
  }

  if (status === 'queued') {
    return {
      uiState: 'queued',
      titleText: 'QUEUEING',
      messageText: "Waiting for GPU to start. Don't worry, this will only take a moment.",
      failedStageIndex: -1,
      isFailedForUI: false,
      interrupted: false,
    };
  }

  if (isInterrupted && status !== 'completed') {
    return {
      uiState: 'interrupted',
      titleText: getInterruptedServersOfflineTitle(),
      messageText: getErrorMessage('Upload', uploadError, true, failedStageIndex),
      failedStageIndex: failedStageIndex >= 0 ? failedStageIndex : 0,
      isFailedForUI: true,
      interrupted: true,
    };
  }

  if (isFailed) {
    const stageLabel = failedStageIndex >= 0 ? stageIndexToLabel(failedStageIndex) : null;
    return {
      uiState: 'failed',
      titleText: stageLabel ? `FAILED AT ${stageLabel}` : 'FAILED',
      messageText: resolveFailureDisplayMessage(failStage || uploadError, {
        isFailed: true,
        failedStageIndex,
      }),
      failedStageIndex,
      isFailedForUI: true,
      interrupted: false,
    };
  }

  return {
    uiState: 'processing',
    titleText: resolvedStageTitle || 'PROCESSING',
    messageText: 'Processing reconstruction…',
    failedStageIndex: -1,
    isFailedForUI: false,
    interrupted: false,
  };
}
